
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Loader2, Plus, CheckCircle, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CustomOfficeInputProps {
  isSelected: boolean;
  customOffice: string;
  showInput: boolean;
  isCreating: boolean;
  onSelect: () => void;
  onCustomOfficeChange: (value: string) => void;
}

const CustomOfficeInput = ({
  isSelected,
  customOffice,
  showInput,
  isCreating,
  onSelect,
  onCustomOfficeChange
}: CustomOfficeInputProps) => {
  
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Input value changed:', value);
    onCustomOfficeChange(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && customOffice.trim().length >= 3) {
      e.preventDefault();
      // Input sudah valid, user bisa proceed
    }
  };

  console.log('CustomOfficeInput render:', { 
    isSelected, 
    customOffice, 
    showInput, 
    isCreating,
    inputLength: customOffice.length 
  });

  return (
    <div
      className={`group relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${
        isSelected 
          ? 'border-sky-600 bg-gradient-to-br from-sky-50 to-sky-100 shadow-md' 
          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
      }`}
      onClick={onSelect}
    >
      {/* Selection Indicator */}
      <div className="flex items-start justify-between mb-4">
        <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-200 ${
          isSelected 
            ? 'border-sky-600 bg-blue-500' 
            : 'border-gray-300 group-hover:border-blue-400'
        }`}>
          {isSelected && (
            <CheckCircle className="w-6 h-6 text-white absolute -top-0.5 -left-0.5" />
          )}
        </div>

        {/* Custom Badge */}
        <div className="flex items-center space-x-2 px-3 py-1.5 bg-sky-100 text-sky-700 border border-sky-200 rounded-full text-sm font-medium">
          <Plus className="w-4 h-4" />
          <span>Custom</span>
        </div>
      </div>

      {/* Custom Office Info */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            isSelected 
              ? 'bg-sky-500 text-white' 
              : 'bg-gray-100 text-gray-600 group-hover:bg-sky-100 group-hover:text-sky-600'
          } transition-all duration-200`}>
            <Plus className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900 group-hover:text-sky-700 transition-colors">
              Create New Site Office
            </h3>
            <p className="text-sm text-gray-600">
              Create a custom site office according to your needs
            </p>
          </div>
        </div>

        {/* Custom Input Field */}
        {showInput && (
          <div className="space-y-3 pt-4 border-t border-gray-100 animate-fadeInUp">
            <label className="block text-sm font-medium text-gray-700">
              Site Office Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type="text"
                placeholder="Enter site office name (minimum 3 characters)..."
                value={customOffice}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className="w-full pl-4 pr-10 py-3 border-2 border-gray-200 rounded-lg focus:border-sky-500 focus:ring-sky-500 transition-all duration-200"
                disabled={isCreating}
                onClick={handleInputClick}
                minLength={3}
                required
                autoFocus
              />
              {isCreating && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Example: Site Office Regional Selatan, SO Jakarta Barat, etc.
              </p>
              <span className={`text-xs ${customOffice.trim().length >= 3 ? 'text-green-600' : 'text-red-500'}`}>
                {customOffice.trim().length}/3 min
              </span>
            </div>
          </div>
        )}

        {/* Office ID Preview */}
        {showInput && customOffice.trim().length >= 3 && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-500">Preview ID:</span>
            <span className="text-sm font-mono font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
              {customOffice.trim().toUpperCase().replace(/\s+/g, '-')}
            </span>
          </div>
        )}
      </div>

      {/* Hover Effect Overlay */}
      <div className={`absolute inset-0 rounded-xl transition-opacity duration-200 ${
        isSelected 
          ? 'bg-gradient-to-br from-sky-500/10 to-blue-500/10' 
          : 'bg-gradient-to-br from-sky-500/0 to-blue-500/0 group-hover:from-sky-500/5 group-hover:to-blue-500/5'
      }`} />
    </div>
  );
};

export default CustomOfficeInput;
